import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2, Upload, ImageIcon, Film, AlertCircle, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { mediaApi } from '@/api/media';
import { useToast } from '@/providers/ToastProvider';
import type { RecipeMedia } from '@/types';

// ── Constants ─────────────────────────────────────────────────────────────────

const ACCEPTED_TYPES = [
  'image/jpeg', 'image/png', 'image/webp', 'image/gif',
  'video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo',
].join(',');

const MAX_PHOTO_BYTES = 50 * 1024 * 1024;   // 50 MB — raw originals allowed; resize runs first
const MAX_VIDEO_BYTES = 200 * 1024 * 1024;  // 200 MB
const MAX_DIMENSION   = 3072;               // px — longest side cap before upload

function isPhoto(mime: string) { return mime.startsWith('image/'); }
function isVideo(mime: string) { return mime.startsWith('video/'); }

// ── Client-side image resize ──────────────────────────────────────────────────
// Scales the image down so its longest side ≤ MAX_DIMENSION, then returns a
// new File. If the image already fits, the original File is returned as-is.
// GIFs are converted to JPEG (canvas can't encode animated GIF).

function resizeImageIfNeeded(file: File): Promise<File> {
  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      const { naturalWidth: w, naturalHeight: h } = img;

      if (w <= MAX_DIMENSION && h <= MAX_DIMENSION) {
        resolve(file);
        return;
      }

      const scale  = MAX_DIMENSION / Math.max(w, h);
      const newW   = Math.round(w * scale);
      const newH   = Math.round(h * scale);

      const canvas  = document.createElement('canvas');
      canvas.width  = newW;
      canvas.height = newH;

      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(file); return; }
      ctx.drawImage(img, 0, 0, newW, newH);

      const outType = file.type === 'image/gif' ? 'image/jpeg' : file.type;
      const quality = outType === 'image/jpeg' || outType === 'image/webp' ? 0.92 : undefined;

      canvas.toBlob(
        (blob) => {
          if (!blob) { resolve(file); return; }
          const ext      = outType === 'image/jpeg' ? 'jpg' : outType.split('/')[1];
          const baseName = file.name.replace(/\.[^.]+$/, '');
          resolve(new File([blob], `${baseName}.${ext}`, { type: outType }));
        },
        outType,
        quality,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);   // fall back to original on decode error
    };

    img.src = objectUrl;
  });
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface UploadingItem {
  id:       string;   // temporary client-side id
  name:     string;
  progress: number;   // 0–100
  error?:   string;
}

// ── XHR upload helper ─────────────────────────────────────────────────────────

function xhrUpload(
  url: string,
  file: File,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (HTTP ${xhr.status})`));
    };
    xhr.onerror  = () => reject(new Error('Network error during upload'));
    xhr.onabort  = () => reject(new Error('Upload aborted'));

    xhr.send(file);
  });
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  recipeId: string;
  canDelete?: boolean;   // false for readonly role
  canUpload?: boolean;   // false to show gallery only
}

export default function RecipeMediaUpload({ recipeId, canDelete = true, canUpload = true }: Props) {
  const { t }         = useTranslation();
  const queryClient   = useQueryClient();
  const { toast }     = useToast();
  const inputRef      = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging]   = useState(false);
  const [uploading, setUploading]     = useState<UploadingItem[]>([]);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  // ── Query ──────────────────────────────────────────────────────────────────

  const { data: mediaList = [], isLoading } = useQuery({
    queryKey: ['recipe-media', recipeId],
    queryFn:  () => mediaApi.list(recipeId),
  });

  // ── Delete mutation ────────────────────────────────────────────────────────

  const deleteMutation = useMutation({
    mutationFn: (mediaId: string) => mediaApi.delete(recipeId, mediaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipe-media', recipeId] });
      toast(t('media.deleted'), 'success');
    },
    onError: () => toast(t('media.deleteError'), 'error'),
  });

  // ── Upload pipeline ────────────────────────────────────────────────────────

  async function uploadFile(file: File) {
    if (!isPhoto(file.type) && !isVideo(file.type)) {
      toast(t('media.unsupportedType'), 'error');
      return;
    }

    // Resize photos client-side before doing any size validation
    const prepared = isPhoto(file.type) ? await resizeImageIfNeeded(file) : file;

    const maxBytes = isPhoto(prepared.type) ? MAX_PHOTO_BYTES : MAX_VIDEO_BYTES;
    if (prepared.size > maxBytes) {
      toast(t('media.fileTooLarge', { max: Math.round(maxBytes / 1024 / 1024) }), 'error');
      return;
    }

    const tmpId = crypto.randomUUID();
    setUploading(prev => [...prev, { id: tmpId, name: prepared.name, progress: 0 }]);

    try {
      // 1 — Ask server for a presigned PUT URL (using resized dimensions)
      const { mediaId, uploadUrl } = await mediaApi.presign(recipeId, {
        filename:    prepared.name,
        contentType: prepared.type,
        sizeBytes:   prepared.size,
      });

      // 2 — Upload directly to Wasabi via XHR (with progress)
      await xhrUpload(uploadUrl, prepared, (pct) =>
        setUploading(prev =>
          prev.map(u => u.id === tmpId ? { ...u, progress: pct } : u),
        ),
      );

      // 3 — Tell server to confirm + generate thumbnail
      await mediaApi.confirm(recipeId, mediaId);

      queryClient.invalidateQueries({ queryKey: ['recipe-media', recipeId] });
      toast(t('media.uploaded'), 'success');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setUploading(prev =>
        prev.map(u => u.id === tmpId ? { ...u, error: msg } : u),
      );
      return;
    } finally {
      // Remove from uploading list after a short delay on success
      setTimeout(() =>
        setUploading(prev => prev.filter(u => u.id !== tmpId)), 800,
      );
    }
  }

  function handleFiles(files: FileList | null) {
    if (!files) return;
    Array.from(files).forEach(uploadFile);
  }

  // ── Drag-and-drop ──────────────────────────────────────────────────────────

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  const allMedia: RecipeMedia[] = mediaList;

  return (
    <div className="space-y-4">

      {/* Drop zone */}
      {canUpload && (
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
        className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-10 cursor-pointer transition-colors
          ${isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/40'}`}
      >
        <Upload size={24} className="text-muted-foreground" />
        <p className="text-sm text-muted-foreground text-center">
          {t('media.dropZoneHint')}
        </p>
        <p className="text-xs text-muted-foreground/70">
          {t('media.dropZoneTypes')}
        </p>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>
      )}

      {/* In-progress uploads */}
      {uploading.length > 0 && (
        <ul className="space-y-2">
          {uploading.map((u) => (
            <li key={u.id} className="rounded-md border px-3 py-2">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="truncate max-w-[70%] text-muted-foreground">{u.name}</span>
                {u.error
                  ? <span className="flex items-center gap-1 text-destructive text-xs"><AlertCircle size={13} />{u.error}</span>
                  : <span className="text-xs text-muted-foreground">{u.progress}%</span>
                }
              </div>
              {!u.error && (
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-200"
                    style={{ width: `${u.progress}%` }}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Media grid */}
      {isLoading ? (
        <p className="text-sm text-center text-muted-foreground py-4">{t('common.loading')}</p>
      ) : allMedia.length === 0 && uploading.length === 0 ? (
        <p className="text-sm text-center text-muted-foreground py-4">{t('media.empty')}</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {allMedia.map((m, idx) => (
            <MediaCard
              key={m.id}
              item={m}
              canDelete={canDelete}
              onDelete={() => deleteMutation.mutate(m.id)}
              isDeleting={deleteMutation.isPending && deleteMutation.variables === m.id}
              onOpen={() => setLightboxIdx(idx)}
            />
          ))}
        </div>
      )}

      {lightboxIdx !== null && (
        <MediaLightbox
          items={allMedia}
          index={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onChange={setLightboxIdx}
        />
      )}
    </div>
  );
}

// ── MediaCard ─────────────────────────────────────────────────────────────────

function MediaCard({
  item, canDelete, onDelete, isDeleting, onOpen,
}: {
  item: RecipeMedia;
  canDelete: boolean;
  onDelete: () => void;
  isDeleting: boolean;
  onOpen: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="group relative rounded-md border bg-muted/30 overflow-hidden">
      <button
        type="button"
        onClick={onOpen}
        className="block w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {item.media_type === 'photo' ? (
          item.thumb_url ? (
            <img
              src={item.thumb_url}
              alt={item.original_name}
              className="w-full aspect-[4/3] object-cover hover:opacity-90 transition-opacity"
              loading="lazy"
            />
          ) : (
            <div className="w-full aspect-[4/3] flex items-center justify-center bg-muted">
              <ImageIcon size={28} className="text-muted-foreground" />
            </div>
          )
        ) : (
          <div className="w-full aspect-[4/3] flex flex-col items-center justify-center gap-2 bg-muted hover:bg-muted/70 transition-colors">
            <Film size={28} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground px-2 text-center truncate w-full">
              {item.original_name}
            </span>
          </div>
        )}
      </button>

      <div className="flex items-center justify-between px-2 py-1.5 border-t bg-card">
        <p className="text-xs text-muted-foreground truncate max-w-[70%]" title={item.original_name}>
          {item.original_name}
        </p>
        {canDelete && (
          <button
            type="button"
            disabled={isDeleting}
            onClick={onDelete}
            title={t('common.delete')}
            className="ml-1 p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
          >
            <Trash2 size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

// ── MediaLightbox ─────────────────────────────────────────────────────────────

function MediaLightbox({
  items, index, onClose, onChange,
}: {
  items: RecipeMedia[];
  index: number;
  onClose: () => void;
  onChange: (i: number) => void;
}) {
  const item = items[index];
  const hasPrev = index > 0;
  const hasNext = index < items.length - 1;

  // Keyboard navigation
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape')      onClose();
      if (e.key === 'ArrowLeft'  && hasPrev) onChange(index - 1);
      if (e.key === 'ArrowRight' && hasNext) onChange(index + 1);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, hasPrev, hasNext, onClose, onChange]);

  if (!item) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Close */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
        aria-label="Close"
      >
        <X size={20} />
      </button>

      {/* Prev */}
      {hasPrev && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onChange(index - 1); }}
          className="absolute left-4 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
          aria-label="Previous"
        >
          <ChevronLeft size={24} />
        </button>
      )}

      {/* Next */}
      {hasNext && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onChange(index + 1); }}
          className="absolute right-4 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
          aria-label="Next"
        >
          <ChevronRight size={24} />
        </button>
      )}

      {/* Content */}
      <div
        className="max-w-[90vw] max-h-[90vh] flex flex-col items-center gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        {item.media_type === 'photo' ? (
          <img
            src={item.url}
            alt={item.original_name}
            className="max-w-full max-h-[80vh] rounded-md object-contain shadow-2xl"
          />
        ) : (
          <video
            src={item.url}
            controls
            autoPlay
            className="max-w-full max-h-[80vh] rounded-md shadow-2xl"
          />
        )}
        <p className="text-sm text-white/70 truncate max-w-[80vw]">{item.original_name}</p>
        {items.length > 1 && (
          <p className="text-xs text-white/50">{index + 1} / {items.length}</p>
        )}
      </div>
    </div>
  );
}
